<template>
  <div id="printable" class="summary-container">
    <!-- Header -->
    <div class="header">
      <div class="facility-name">
        TUURU COTTOLENGO HEALTH CENTRE - AR2026259
      </div>
      <div class="facility-details">
        <div>Igembe Central</div>
        <div>P.O BOX 77 - Meru | Tel: 254254746758069</div>
        <div>Email: suore.tuuru@cottolengo.org</div>
      </div>
      <div class="register-title">
        MOH 711A - REPRODUCTIVE & MATERNAL HEALTH SUMMARY
      </div>
    </div>

    <!-- Month/Year and Generated By Bar -->
    <div class="info-bar">
      <div class="info-left">
        <div class="info-item">
          <span class="info-label">Month:</span>
          <select v-model="selectedMonth" class="month-select">
            <option value="January">January</option>
            <option value="February">February</option>
            <option value="March">March</option>
            <option value="April">April</option>
            <option value="May">May</option>
            <option value="June">June</option>
            <option value="July">July</option>
            <option value="August">August</option>
            <option value="September">September</option>
            <option value="October">October</option>
            <option value="November">November</option>
            <option value="December">December</option>
          </select>
        </div>
        <div class="info-item">
          <span class="info-label">Year:</span>
          <input
            type="number"
            v-model="selectedYear"
            class="year-input"
            min="2020"
            max="2030"
          />
        </div>
        <div class="info-item">
          <span class="info-label">Generated By:</span>
          <span class="info-value">{{ generatedBy }}</span>
        </div>
      </div>
      <button @click="printPage" class="btn-print">Print Summary</button>
    </div>

    <!-- Period Display for Print -->
    <div class="print-period">
      <strong>Reporting Period:</strong> {{ selectedMonth }} {{ selectedYear }}
    </div>

    <!-- Section 1: ANC Indicators -->
    <div class="section">
      <div class="section-title">1. ANTENATAL CARE (ANC) INDICATORS</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">New ANC Enrollees</div>
          <div class="stat-value">{{ anc.newEnrollees }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total ANC Visits</div>
          <div class="stat-value">{{ anc.totalVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">ANC 1st Trimester</div>
          <div class="stat-value">{{ anc.firstTrimester }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">ANC 4+ Visits</div>
          <div class="stat-value">{{ anc.fourPlusVisits }}</div>
        </div>
      </div>
    </div>

    <!-- Section 2: Intermittent Preventive Treatment (IPTp) -->
    <div class="section">
      <div class="section-title">
        2. INTERMITTENT PREVENTIVE TREATMENT (IPTp)
      </div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Number</th>
              <th>Coverage (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ipt in iptpData" :key="ipt.name">
              <td>{{ ipt.name }}</td>
              <td>{{ ipt.number }}</td>
              <td>{{ ipt.coverage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 3: Tetanus Toxoid (TT) Vaccination -->
    <div class="section">
      <div class="section-title">
        3. TETANUS TOXOID (TT) VACCINATION FOR PREGNANT WOMEN
      </div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Dose</th>
              <th>Target</th>
              <th>Given</th>
              <th>Coverage (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tt in ttData" :key="tt.dose">
              <td>{{ tt.dose }}</td>
              <td>{{ tt.target }}</td>
              <td>{{ tt.given }}</td>
              <td>{{ tt.coverage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 4: Delivery Indicators -->
    <div class="section">
      <div class="section-title">4. DELIVERY INDICATORS</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Deliveries</div>
          <div class="stat-value">{{ delivery.totalDeliveries }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">SVD (Normal)</div>
          <div class="stat-value">{{ delivery.svd }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">C-Section</div>
          <div class="stat-value">{{ delivery.cSection }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Assisted Delivery</div>
          <div class="stat-value">{{ delivery.assisted }}</div>
        </div>
      </div>
    </div>

    <!-- Section 5: Birth Outcomes -->
    <div class="section">
      <div class="section-title">5. BIRTH OUTCOMES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Live Births</div>
          <div class="stat-value">{{ birthOutcomes.liveBirths }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Stillbirths</div>
          <div class="stat-value">{{ birthOutcomes.stillbirths }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Fresh Stillbirths</div>
          <div class="stat-value">{{ birthOutcomes.freshStillbirths }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Macerated Stillbirths</div>
          <div class="stat-value">{{ birthOutcomes.maceratedStillbirths }}</div>
        </div>
      </div>
    </div>

    <!-- Section 6: Maternal and Perinatal Deaths -->
    <div class="section">
      <div class="section-title">6. MATERNAL AND PERINATAL DEATHS</div>
      <div class="stats-grid">
        <div class="stat-card warning">
          <div class="stat-label">Maternal Deaths</div>
          <div class="stat-value">{{ deaths.maternalDeaths }}</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-label">Early Neonatal Deaths</div>
          <div class="stat-value">{{ deaths.earlyNeonatal }}</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-label">Late Neonatal Deaths</div>
          <div class="stat-value">{{ deaths.lateNeonatal }}</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-label">Perinatal Deaths</div>
          <div class="stat-value">{{ deaths.perinatal }}</div>
        </div>
      </div>
    </div>

    <!-- Section 7: Postnatal Care (PNC) -->
    <div class="section">
      <div class="section-title">7. POSTNATAL CARE (PNC) INDICATORS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Number</th>
              <th>Coverage (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pnc in pncData" :key="pnc.name">
              <td>{{ pnc.name }}</td>
              <td>{{ pnc.number }}</td>
              <td>{{ pnc.coverage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 8: Family Planning -->
    <div class="section">
      <div class="section-title">8. FAMILY PLANNING SERVICES</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Method</th>
              <th>New Acceptors</th>
              <th>Continuing Users</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fp in familyPlanning" :key="fp.method">
              <td>{{ fp.method }}</td>
              <td>{{ fp.newAcceptors }}</td>
              <td>{{ fp.continuingUsers }}</td>
              <td>{{ fp.newAcceptors + fp.continuingUsers }}</td>
            </tr>
            <tr class="total-row">
              <td><strong>Total</strong></td>
              <td>
                <strong>{{ totalFamilyPlanning.newAcceptors }}</strong>
              </td>
              <td>
                <strong>{{ totalFamilyPlanning.continuingUsers }}</strong>
              </td>
              <td>
                <strong>{{ totalFamilyPlanning.total }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 9: PMTCT Indicators -->
    <div class="section">
      <div class="section-title">9. PMTCT INDICATORS</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">HIV+ Pregnant Women</div>
          <div class="stat-value">{{ pmtct.hivPositivePregnant }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">On ART</div>
          <div class="stat-value">{{ pmtct.onArt }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">HIV Exposed Infants</div>
          <div class="stat-value">{{ pmtct.hivExposedInfants }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">HEI on Prophylaxis</div>
          <div class="stat-value">{{ pmtct.heiOnProphylaxis }}</div>
        </div>
      </div>
    </div>

    <!-- Section 10: Adolescent and Youth Services -->
    <div class="section">
      <div class="section-title">
        10. ADOLESCENT AND YOUTH SERVICES (10-24 Years)
      </div>
      <div class="stats-grid-2">
        <div class="stat-card">
          <div class="stat-label">Adolescent ANC Visits</div>
          <div class="stat-value">{{ adolescent.ancVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Adolescent Deliveries</div>
          <div class="stat-value">{{ adolescent.deliveries }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Adolescent PNC Visits</div>
          <div class="stat-value">{{ adolescent.pncVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Adolescent FP Acceptors</div>
          <div class="stat-value">{{ adolescent.fpAcceptors }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOH711ASummary",
  data() {
    return {
      selectedMonth: "April",
      selectedYear: 2026,
      generatedBy: "SR. JANE KAREGI MUNYUGI",

      anc: {
        newEnrollees: 187,
        totalVisits: 845,
        firstTrimester: 142,
        fourPlusVisits: 165,
      },

      iptpData: [
        { name: "IPTp 1", number: 175, coverage: 93.6 },
        { name: "IPTp 2", number: 168, coverage: 89.8 },
        { name: "IPTp 3+", number: 142, coverage: 75.9 },
      ],

      ttData: [
        { dose: "TT 1", target: 187, given: 182, coverage: 97.3 },
        { dose: "TT 2", target: 185, given: 178, coverage: 96.2 },
        { dose: "TT 3", target: 180, given: 170, coverage: 94.4 },
        { dose: "TT 4", target: 175, given: 162, coverage: 92.6 },
        { dose: "TT 5", target: 170, given: 155, coverage: 91.2 },
      ],

      delivery: {
        totalDeliveries: 156,
        svd: 138,
        cSection: 16,
        assisted: 2,
      },

      birthOutcomes: {
        liveBirths: 152,
        stillbirths: 4,
        freshStillbirths: 2,
        maceratedStillbirths: 2,
      },

      deaths: {
        maternalDeaths: 1,
        earlyNeonatal: 2,
        lateNeonatal: 1,
        perinatal: 6,
      },

      pncData: [
        { name: "PNC within 48 hours", number: 145, coverage: 92.9 },
        { name: "PNC Day 7", number: 138, coverage: 88.5 },
        { name: "PNC Day 42", number: 125, coverage: 80.1 },
        {
          name: "Exclusive Breastfeeding (6 weeks)",
          number: 140,
          coverage: 89.7,
        },
      ],

      familyPlanning: [
        { method: "Pills (COC/POP)", newAcceptors: 45, continuingUsers: 210 },
        { method: "Injectable (DMPA)", newAcceptors: 52, continuingUsers: 245 },
        { method: "Implants", newAcceptors: 28, continuingUsers: 98 },
        { method: "IUD", newAcceptors: 8, continuingUsers: 35 },
        {
          method: "Condoms (Male/Female)",
          newAcceptors: 35,
          continuingUsers: 65,
        },
        { method: "Natural Methods", newAcceptors: 12, continuingUsers: 28 },
        {
          method: "Permanent (BTL/Vasectomy)",
          newAcceptors: 2,
          continuingUsers: 0,
        },
      ],

      pmtct: {
        hivPositivePregnant: 18,
        onArt: 18,
        hivExposedInfants: 16,
        heiOnProphylaxis: 16,
      },

      adolescent: {
        ancVisits: 42,
        deliveries: 28,
        pncVisits: 35,
        fpAcceptors: 38,
      },
    };
  },
  computed: {
    totalFamilyPlanning() {
      let newAcceptors = 0;
      let continuingUsers = 0;
      this.familyPlanning.forEach((fp) => {
        newAcceptors += fp.newAcceptors;
        continuingUsers += fp.continuingUsers;
      });
      return {
        newAcceptors: newAcceptors,
        continuingUsers: continuingUsers,
        total: newAcceptors + continuingUsers,
      };
    },
  },
  methods: {
    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.summary-container {
  background: white;
  min-height: 100vh;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a56db;
}

.facility-name {
  font-size: 20px;
  font-weight: bold;
  color: #1a56db;
  margin-bottom: 6px;
}

.facility-details {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 10px;
}

.register-title {
  font-size: 14px;
  font-weight: bold;
  background: #f3f4f6;
  display: inline-block;
  padding: 5px 16px;
  border-radius: 20px;
  color: #1f2937;
}

.info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.info-left {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.month-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: white;
}

.year-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  width: 70px;
}

.btn-print {
  padding: 8px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-print:hover {
  background: #2563eb;
}

.print-period {
  text-align: center;
  padding: 8px;
  margin-bottom: 20px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  font-size: 12px;
  color: #92400e;
  border-radius: 6px;
}

.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #1f2937;
  background: #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.stats-grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
}

.stat-card.warning {
  background: #fef3c7;
  border-color: #fde68a;
}

.stat-card.warning .stat-value {
  color: #d97706;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-card.warning .stat-label {
  color: #92400e;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #059669;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.data-table th {
  background: #1f2937;
  color: white;
  padding: 8px;
  text-align: center;
  border: 1px solid #374151;
}

.data-table td {
  border: 1px solid #d1d5db;
  padding: 8px;
  text-align: center;
}

.data-table td:first-child {
  text-align: left;
  font-weight: 500;
}

.total-row {
  background: #fef3c7;
  font-weight: bold;
}

@media print {
  @page {
    size: portrait;
    margin: 0.5cm;
  }

  .btn-print,
  .month-select,
  .year-input {
    display: none;
  }

  .info-bar {
    display: none;
  }

  .print-period {
    background: #fef3c7;
    margin: 10px 0;
    padding: 5px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-container {
    padding: 0.2cm;
  }

  .header {
    margin-bottom: 5px;
  }

  .facility-name {
    font-size: 14px;
  }

  .facility-details {
    font-size: 8px;
  }

  .register-title {
    font-size: 10px;
  }

  .section-title {
    font-size: 11px;
    padding: 4px 8px;
  }

  .stat-value {
    font-size: 18px;
  }

  .stat-label {
    font-size: 9px;
  }

  .data-table {
    font-size: 9px;
  }

  .data-table th,
  .data-table td {
    padding: 4px;
  }

  .stat-card {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
